import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Book, Calendar, Clock, RotateCcw, Info, Hash, User, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { studentService } from '../../services/studentService';

const Library = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('current'); // 'current' or 'history'
    const [loading, setLoading] = useState(true);

    // Professional Dummy Data
    const libraryData = {
        current: [
            {
                id: 1,
                title: "Fundamentals of Physics",
                author: "Halliday, Resnick, and Walker",
                category: "Science",
                issueDate: "2026-01-25",
                dueDate: "2026-02-08",
                accessionNo: "LIB-PH-402",
                status: "Issued",
                daysLeft: 5
            },
            {
                id: 2,
                title: "Wings of Fire",
                author: "A.P.J. Abdul Kalam",
                category: "Autobiography",
                issueDate: "2026-02-01",
                dueDate: "2026-02-15",
                accessionNo: "LIB-BIO-105",
                status: "Issued",
                daysLeft: 12
            }
        ],
        history: [
            {
                id: 101,
                title: "To Kill a Mockingbird",
                author: "Harper Lee",
                category: "Fiction",
                issueDate: "2026-01-05",
                returnDate: "2026-01-18",
                dueDate: "2026-01-19",
                accessionNo: "LIB-FIC-088",
                status: "Returned"
            },
            {
                id: 102,
                title: "Numerical Mathematics",
                author: "G. Dahlquist",
                category: "Mathematics",
                issueDate: "2025-12-10",
                returnDate: "2025-12-24",
                dueDate: "2025-12-24",
                accessionNo: "LIB-MA-210",
                status: "Returned"
            },
            {
                id: 103,
                title: "Advanced Organic Chemistry",
                author: "Francis A. Carey",
                category: "Science",
                issueDate: "2025-12-01",
                dueDate: "2025-12-15",
                returnDate: "2025-12-20",
                accessionNo: "LIB-CH-305",
                status: "Returned Late",
                fine: 25
            }
        ]
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        // Simulate loading
        const timer = setTimeout(() => setLoading(false), 500);
        return () => clearTimeout(timer);
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-pragyaBlue/20 border-t-pragyaBlue rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-32">
            {/* Header Section */}
            <div className="bg-white px-6 pt-6 pb-2 rounded-b-[2.5rem] shadow-sm border-b border-slate-100 sticky top-0 z-30">
                <div className="flex items-center justify-between mb-6">
                    <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-slate-50">
                        <ChevronLeft size={24} className="text-slate-600" />
                    </button>
                    <h2 className="text-lg font-bold text-slate-800">Library Portal</h2>
                    <div className="w-10" />
                </div>

                {/* Tabs */}
                <div className="flex p-1 bg-slate-100 rounded-2xl mb-4">
                    <button
                        onClick={() => setActiveTab('current')}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'current' ? 'bg-white text-pragyaBlue shadow-sm' : 'text-slate-500'}`}
                    >
                        Currently Issued
                        {libraryData.current.length > 0 && (
                            <span className="ml-2 bg-pragyaBlue/10 text-pragyaBlue px-2 py-0.5 rounded-full text-[10px]">
                                {libraryData.current.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'history' ? 'bg-white text-pragyaBlue shadow-sm' : 'text-slate-500'}`}
                    >
                        Issue History
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-5 mt-6">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                    >
                        {activeTab === 'current' ? (
                            libraryData.current.map((book) => (
                                <div key={book.id} className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 overflow-hidden relative group">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-pragyaBlue/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110" />

                                    <div className="flex gap-4 relative z-10">
                                        <div className="w-20 h-28 bg-slate-100 rounded-xl flex flex-col items-center justify-center text-slate-300 border border-slate-200 shadow-inner">
                                            <Book size={32} />
                                            <span className="text-[8px] font-black mt-2 uppercase tracking-tighter">Library</span>
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="text-[10px] font-black text-pragyaBlue uppercase px-2 py-0.5 bg-pragyaBlue/5 rounded-md">
                                                    {book.category}
                                                </span>
                                                <div className="flex items-center gap-1 text-[10px] font-bold text-orange-500">
                                                    <Clock size={12} />
                                                    {book.daysLeft} days left
                                                </div>
                                            </div>
                                            <h3 className="text-sm font-extrabold text-slate-800 mb-0.5 leading-snug">{book.title}</h3>
                                            <p className="text-slate-400 text-[11px] font-semibold mb-3 flex items-center gap-1">
                                                <User size={10} /> {book.author}
                                            </p>

                                            <div className="grid grid-cols-2 gap-2 mt-auto">
                                                <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                                                    <p className="text-[8px] font-black text-slate-300 uppercase leading-none mb-1">Issued On</p>
                                                    <p className="text-[10px] font-bold text-slate-600">{book.issueDate}</p>
                                                </div>
                                                <div className="bg-orange-50 p-2 rounded-lg border border-orange-100">
                                                    <p className="text-[8px] font-black text-orange-300 uppercase leading-none mb-1">Due Date</p>
                                                    <p className="text-[10px] font-bold text-orange-600">{book.dueDate}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 flex items-center justify-between pt-4 border-t border-slate-50 italic">
                                        <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                                            <Hash size={10} /> Accession: {book.accessionNo}
                                        </div>
                                        <button className="text-[10px] font-bold text-pragyaBlue hover:underline">
                                            Request Renewal
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            libraryData.history.map((book) => (
                                <div key={book.id} className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 flex flex-col gap-3 opacity-90">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${book.fine ? 'bg-red-50 text-red-500' : 'bg-slate-50 text-slate-400'}`}>
                                            <RotateCcw size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-sm font-bold text-slate-800 mb-1 leading-snug">{book.title}</h3>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="text-slate-400 text-[11px] font-medium italic">Returned: {book.returnDate}</p>
                                                {book.fine && (
                                                    <span className="text-[11px] font-black text-white bg-red-500 px-2 py-0.5 rounded-lg shadow-sm shadow-red-200">
                                                        Fine Paid: ₹{book.fine}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md ${book.status === 'Returned Late' ? 'text-orange-500 bg-orange-50' : 'text-green-500 bg-green-50'}`}>
                                                {book.status}
                                            </span>
                                        </div>
                                    </div>
                                    {book.fine && (
                                        <div className="bg-red-50/50 p-2 rounded-xl flex items-center gap-2 border border-red-50">
                                            <AlertTriangle size={12} className="text-red-400" />
                                            <p className="text-[10px] text-red-600 font-semibold">Fine of ₹{book.fine} was charged for 5 days delay.</p>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}

                        {libraryData[activeTab].length === 0 && (
                            <div className="text-center py-20">
                                <AlertTriangle size={48} className="mx-auto text-slate-200 mb-4" />
                                <h3 className="text-slate-400 font-bold">No library records</h3>
                                <p className="text-slate-300 text-xs">Your library account is currently clear.</p>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Library Guidelines */}
            <div className="px-5 mt-8">
                <div className="bg-pragyaBlue/5 rounded-3xl p-5 border border-pragyaBlue/10">
                    <div className="flex items-center gap-3 mb-3 text-pragyaBlue">
                        <Info size={18} />
                        <h4 className="font-bold text-sm">Library Rules</h4>
                    </div>
                    <ul className="space-y-2">
                        <li className="text-[11px] text-slate-500 font-medium flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-pragyaBlue mt-1.5" />
                            Books must be returned within 14 days of issue.
                        </li>
                        <li className="text-[11px] text-slate-500 font-medium flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-pragyaBlue mt-1.5" />
                            A fine of ₹5/day is applicable after the due date.
                        </li>
                        <li className="text-[11px] text-slate-500 font-medium flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-pragyaBlue mt-1.5" />
                            Renewals are allowed once per book if not reserved.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Library;
